import type { Feeding, PumpingSession, SleepSession, TimelineItem } from '@baby-tracker/contracts';
import type {
    IanaTimeZone,
    MassDisplayUnit,
    MilkType,
    VolumeDisplayUnit,
} from '@baby-tracker/validation';
import { Baby, BedDouble, Droplets, Milk, Soup, Wind } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { useNow } from '../../../hooks/useNow.js';
import {
    useCreateFeeding,
    useCreateNappy,
    useCreatePumping,
    useCreateSleep,
    useDeleteFeeding,
    useDeleteNappy,
    useDeletePumping,
    useDeleteSleep,
} from '../../../hooks/useTracking.js';
import { suggestedSleepKind } from '../../../lib/calendar.js';
import { lastHint, lastLoggedAt, recentBottleAmounts } from '../../../lib/last-logged.js';
import { ActionTile } from '../../ui/ActionTile.js';
import { Button } from '../../ui/Button.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import { useToast } from '../../ui/Toast.js';
import { BottleSheet } from './BottleSheet.js';
import { NappySheet, type NappySave } from './NappySheet.js';
import { SleepSheet } from './SleepSheet.js';
import { SolidSheet } from './SolidSheet.js';
import styles from './tracking.module.css';

type SheetName = 'bottle' | 'solid' | 'sleep' | 'nappy' | null;

export function QuickLog({
    familyId,
    childId,
    childName,
    volumeUnit,
    massUnit,
    timeZone,
    timeline,
    openFeeding,
    openSleep,
    openPump,
}: {
    familyId: string;
    childId: string;
    childName: string;
    volumeUnit: VolumeDisplayUnit;
    massUnit: MassDisplayUnit;
    timeZone: IanaTimeZone;
    timeline: TimelineItem[];
    openFeeding: Feeding | null;
    openSleep: SleepSession | null;
    openPump: PumpingSession | null;
}): ReactElement {
    const [sheet, setSheet] = useState<SheetName>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const now = useNow(true);
    const toast = useToast();
    const createFeeding = useCreateFeeding(familyId);
    const createSleep = useCreateSleep(familyId);
    const createNappy = useCreateNappy(familyId);
    const createPump = useCreatePumping(familyId);
    const deleteFeeding = useDeleteFeeding(familyId);
    const deleteSleep = useDeleteSleep(familyId);
    const deleteNappy = useDeleteNappy(familyId);
    const deletePump = useDeletePumping(familyId);
    const suggestion = suggestedSleepKind(timeZone);

    async function run<T>(task: () => Promise<T>): Promise<T | undefined> {
        setActionError(null);
        try {
            return await task();
        } catch (error) {
            setActionError(errorText(error));
            return undefined;
        }
    }

    function saved(message: string, undo: () => Promise<unknown>): void {
        setSheet(null);
        toast.showToast({
            message,
            actionLabel: 'Undo',
            onAction: () => {
                void undo();
            },
        });
    }

    return (
        <div role="group" aria-label={`Log for ${childName}`} className={styles.quick}>
            <ActionTile
                icon={Baby}
                label="Breast"
                hint={lastHint(lastLoggedAt(timeline, childId, 'breast'), now)}
                loading={createFeeding.isPending}
                disabled={openFeeding !== null}
                onClick={() => {
                    void run(async () => {
                        const row = await createFeeding.mutateAsync({ method: 'breast', childId });
                        saved('Breast started', () => deleteFeeding.mutateAsync(row.id));
                    });
                }}
            />
            <ActionTile
                icon={Milk}
                label="Bottle"
                hint={lastHint(lastLoggedAt(timeline, childId, 'bottle'), now)}
                onClick={() => {
                    setSheet('bottle');
                }}
            />
            <ActionTile
                icon={Soup}
                label="Solid"
                hint={lastHint(lastLoggedAt(timeline, childId, 'solid'), now)}
                onClick={() => {
                    setSheet('solid');
                }}
            />
            <ActionTile
                icon={BedDouble}
                label="Sleep"
                hint={lastHint(lastLoggedAt(timeline, childId, 'sleep'), now)}
                loading={createSleep.isPending}
                disabled={openSleep !== null}
                onClick={() => {
                    void run(async () => {
                        const row = await createSleep.mutateAsync({ childId, kind: suggestion });
                        saved('Sleep started', () => deleteSleep.mutateAsync(row.id));
                    });
                }}
            />
            <ActionTile
                icon={Droplets}
                label="Nappy"
                hint={lastHint(lastLoggedAt(timeline, childId, 'nappy'), now)}
                onClick={() => {
                    setSheet('nappy');
                }}
            />
            <ActionTile
                icon={Wind}
                label="Pump"
                hint={lastHint(lastLoggedAt(timeline, childId, 'pump'), now)}
                loading={createPump.isPending}
                disabled={openPump !== null}
                onClick={() => {
                    void run(async () => {
                        const row = await createPump.mutateAsync({});
                        saved('Pump started', () => deletePump.mutateAsync(row.id));
                    });
                }}
            />
            <div className={styles.quickExtra}>
                <Button
                    variant="ghost"
                    onClick={() => {
                        setSheet('sleep');
                    }}
                >
                    Enter sleep times
                </Button>
            </div>
            <FormError message={actionError} />
            <BottleSheet
                open={sheet === 'bottle'}
                unit={volumeUnit}
                recentMl={recentBottleAmounts(timeline, childId)}
                pending={createFeeding.isPending}
                error={actionError}
                onClose={() => {
                    setSheet(null);
                }}
                onSave={async (input: { milkType: MilkType; volumeMl: number }) => {
                    await run(async () => {
                        const row = await createFeeding.mutateAsync({
                            method: 'bottle',
                            childId,
                            milkType: input.milkType,
                            volumeMl: input.volumeMl,
                        });
                        saved('Bottle saved', () => deleteFeeding.mutateAsync(row.id));
                    });
                }}
            />
            <SolidSheet
                open={sheet === 'solid'}
                unit={massUnit}
                pending={createFeeding.isPending}
                error={actionError}
                onClose={() => {
                    setSheet(null);
                }}
                onSave={async (input) => {
                    await run(async () => {
                        const row = await createFeeding.mutateAsync({
                            method: 'solid',
                            childId,
                            foodDescription: input.foodDescription,
                            ...(input.amountG !== undefined ? { amountG: input.amountG } : {}),
                        });
                        saved('Solid saved', () => deleteFeeding.mutateAsync(row.id));
                    });
                }}
            />
            <SleepSheet
                open={sheet === 'sleep'}
                suggestion={suggestion}
                timeZone={timeZone}
                pending={createSleep.isPending}
                error={actionError}
                onClose={() => {
                    setSheet(null);
                }}
                onStart={async (kind) => {
                    await run(async () => {
                        const row = await createSleep.mutateAsync({ childId, kind });
                        saved('Sleep started', () => deleteSleep.mutateAsync(row.id));
                    });
                }}
                onComplete={async (values) => {
                    await run(async () => {
                        const row = await createSleep.mutateAsync({
                            childId,
                            kind: values.kind,
                            startAt: values.startAt,
                            endAt: values.endAt,
                        });
                        saved('Sleep saved', () => deleteSleep.mutateAsync(row.id));
                    });
                }}
            />
            <NappySheet
                open={sheet === 'nappy'}
                pending={createNappy.isPending}
                error={actionError}
                onClose={() => {
                    setSheet(null);
                }}
                onSave={async (input: NappySave) => {
                    await run(async () => {
                        const row = await createNappy.mutateAsync({
                            childId,
                            kind: input.kind,
                            hasRash: input.hasRash,
                            ...(input.stoolColour !== undefined
                                ? { stoolColour: input.stoolColour }
                                : {}),
                            ...(input.stoolTexture !== undefined
                                ? { stoolTexture: input.stoolTexture }
                                : {}),
                        });
                        saved('Nappy saved', () => deleteNappy.mutateAsync(row.id));
                    });
                }}
            />
        </div>
    );
}
